import { type TypedDocumentNode } from '@graphql-typed-document-node/core'

import useMutation from '@hooks/useGQLMutation'
import { type ErrorType, useErrorsStore } from '@stores/errorsStore'

import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('roster_board')

const DEFAULT_ERRORS_PATH = 'rosterAssignment'

type RunValues = {
  onSuccess: () => void
  onError?: () => void
} & Record<string, unknown>

type RosterActionConfig<TResult> = {
  document: TypedDocumentNode<TResult, any>
  succeeded: (response: TResult) => boolean
  errorCode: string
  failureMessage?: string
  messageKey?: string
  errorsPath?: string
  extractErrors?: (response: TResult) => ErrorType[] | null | undefined
  interceptErrors?: (errors: ErrorType[]) => boolean
}

export function useRosterActionMutation<TResult extends Record<string, unknown>>({
  document,
  succeeded,
  errorCode,
  failureMessage,
  messageKey,
  errorsPath = DEFAULT_ERRORS_PATH,
  extractErrors,
  interceptErrors,
}: RosterActionConfig<TResult>) {
  const { setErrors, clearErrors } = useErrorsStore(({ setErrors, clearErrors }) => ({ setErrors, clearErrors }))
  const { mutate, isLoading } = useMutation<typeof document>(document)

  const message = failureMessage ?? t(messageKey ?? '')

  const fail = (serverErrors: ErrorType[] | null | undefined, onError?: () => void) => {
    if (serverErrors?.length && interceptErrors?.(serverErrors)) {
      onError?.()
      return
    }
    setErrors({
      errorsPath,
      errors: serverErrors?.length ? serverErrors : [{ code: errorCode, message }],
    })
    onError?.()
  }

  const run = ({ onSuccess, onError, ...input }: RunValues) => {
    clearErrors(errorsPath)
    mutate(
      { input },
      {
        onSuccess: (response) => (succeeded(response) ? onSuccess() : fail(extractErrors?.(response), onError)),
        onError: () => fail(null, onError),
      }
    )
  }

  return { run, isPending: isLoading }
}
